<template>
  <HeaderComponent />
  <div class="fixed top-[80px] left-[50%] z-[100] translate-x-[-50%] w-[80%] max-w-[400px] flex flex-col">
    <div v-if="errorMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-red-700 my-[10px] font-bold text-center p-[20px]">{{ errorMessage }}</div>
    <div v-if="successMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-green-700 my-[10px] font-bold text-center p-[20px]">{{ successMessage }}</div>
  </div>

  <section class="w-full min-h-screen pt-[100px] pb-[60px] bg-gray-100 flex justify-center">
    <div class="container lg:max-w-[1200px] px-[20px] w-full">
      <!-- En-tête -->
      <div class="w-full flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-[20px]">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">Bonjour, {{ client?.nom }}</h1>
          <p class="text-sm text-gray-500">Suivez vos paiements échelonnés</p>
        </div>
        <div class="flex gap-2">
          <button @click="showDemandeModal = true" class="px-[15px] py-[10px] rounded-[5px] text-sm font-bold bg-orange-600 text-white hover:bg-orange-700 transition-colors">
            <i class="fa-solid fa-magnifying-glass mr-[5px]"></i> Demander un produit
          </button>
          <button @click="logout" class="px-[15px] py-[10px] rounded-[5px] text-sm font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors">
            <i class="fa-solid fa-right-from-bracket"></i>
          </button>
        </div>
      </div>

      <!-- Onglets -->
      <div class="inline-flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 mb-[20px]">
        <button @click="activeTab = 'plans'" :class="activeTab === 'plans' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-bold transition-colors">Mes plans</button>
        <button @click="switchToPaiements" :class="activeTab === 'paiements' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-bold transition-colors">Mes paiements</button>
        <button @click="switchToAchats" :class="activeTab === 'achats' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'" class="px-4 py-2 rounded-md text-sm font-bold transition-colors">Mes achats</button>
      </div>

      <!-- ===================== ONGLET PLANS ===================== -->
      <div v-if="activeTab === 'plans'">
        <div v-if="isLoading" class="w-full h-[200px] flex justify-center items-center">
          <div class="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else-if="plans.length === 0" class="w-full bg-white rounded-[10px] shadow-4xl p-[40px] flex flex-col items-center text-center">
          <i class="fa-solid fa-mobile-screen-button text-4xl text-orange-400 mb-[15px]"></i>
          <h2 class="text-lg font-bold text-gray-700">Aucun plan de paiement</h2>
          <p class="text-sm text-gray-500 mt-[10px]">Choisissez un téléphone et payez par tranches, ou faites une demande.</p>
          <router-link to="/boutique" class="mt-[20px] px-[20px] py-[12px] rounded-[5px] bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold transition-colors">Voir la boutique</router-link>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
          <div v-for="plan in plans" :key="plan._id" class="bg-white rounded-[10px] shadow-4xl overflow-hidden flex flex-col">
            <div class="flex p-[15px] gap-[15px]">
              <div class="w-[80px] h-[80px] min-w-[80px] bg-gray-100 rounded-[8px] overflow-hidden flex items-center justify-center">
                <img v-if="plan.produit?.photo" :src="plan.produit.photo" class="w-full h-full object-cover" alt="">
                <i v-else class="fa-solid fa-mobile-screen-button text-2xl text-gray-300"></i>
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <h3 class="font-bold text-gray-800 text-sm">{{ plan.produit?.nom || plan.nomDemande }}</h3>
                  <span :class="statutClass(plan.statut)" class="text-[10px] px-2 py-1 rounded-full font-bold whitespace-nowrap ml-2">{{ statutLabel(plan.statut) }}</span>
                </div>
                <p v-if="plan.produit?.categorie" class="text-xs text-gray-500 mt-[3px]">{{ plan.produit.categorie }}</p>
                <p v-if="plan.prixTotal" class="text-sm font-bold text-gray-800 mt-[5px]">{{ formatPrice(plan.prixTotal) }}</p>
              </div>
            </div>

            <div v-if="plan.prixTotal > 0" class="px-[15px] pb-[10px]">
              <div class="w-full h-[8px] bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full bg-orange-500 rounded-full transition-all" :style="{ width: progress(plan) + '%' }"></div>
              </div>
              <div class="flex justify-between text-xs mt-[6px]">
                <span class="text-green-600 font-semibold">Payé : {{ formatPrice(plan.montantPaye) }}</span>
                <span class="text-gray-500 font-semibold">Reste : {{ formatPrice(reste(plan)) }}</span>
              </div>
            </div>

            <div class="mt-auto p-[15px] pt-[5px]">
              <button v-if="['a_payer','en_cours'].includes(plan.statut)" @click="openPayment(plan)" class="w-full py-[10px] rounded-[5px] bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold transition-colors">
                <i class="fa-solid fa-money-bill-wave mr-[5px]"></i> Payer une tranche
              </button>
              <div v-else-if="plan.statut === 'demande'" class="w-full py-[10px] rounded-[5px] bg-yellow-50 text-yellow-700 text-xs font-semibold text-center">En attente : nous recherchons ce produit pour vous.</div>
              <div v-else-if="plan.statut === 'termine'" class="w-full py-[10px] rounded-[5px] bg-green-50 text-green-700 text-xs font-semibold text-center">Entièrement payé — passez le récupérer en boutique.</div>
              <div v-else-if="plan.statut === 'vendu'" class="w-full py-[10px] rounded-[5px] bg-gray-100 text-gray-500 text-xs font-semibold text-center">Téléphone récupéré. Merci !</div>

              <button v-if="canRemove(plan)" @click="removePlan(plan)" class="w-full mt-[8px] py-[8px] rounded-[5px] text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors">
                <i class="fa-solid fa-trash mr-[4px]"></i> Retirer ce plan
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===================== ONGLET PAIEMENTS ===================== -->
      <div v-else-if="activeTab === 'paiements'">
        <div v-if="isLoadingVers" class="w-full h-[200px] flex justify-center items-center">
          <div class="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else-if="versements.length === 0" class="w-full bg-white rounded-[10px] shadow-4xl p-[40px] flex flex-col items-center text-center">
          <i class="fa-solid fa-receipt text-4xl text-orange-400 mb-[15px]"></i>
          <h2 class="text-lg font-bold text-gray-700">Aucun paiement effectué</h2>
          <p class="text-sm text-gray-500 mt-[10px]">Vos versements apparaîtront ici avec leur justificatif et leur statut.</p>
        </div>

        <div v-else class="w-full bg-white rounded-[10px] shadow-4xl overflow-hidden">
          <div v-for="v in versements" :key="v._id" class="flex items-center gap-[15px] p-[15px] border-b border-gray-50 last:border-b-0">
            <!-- Justificatif -->
            <button @click="openImage(v.preuve)" class="w-[60px] h-[60px] min-w-[60px] bg-gray-100 rounded-[8px] overflow-hidden flex items-center justify-center relative group">
              <img v-if="v.preuve" :src="v.preuve" class="w-full h-full object-cover" alt="justificatif">
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all">
                <i class="fa-solid fa-magnifying-glass-plus text-white opacity-0 group-hover:opacity-100"></i>
              </div>
            </button>
            <!-- Infos -->
            <div class="flex-1 min-w-0">
              <p class="font-bold text-gray-800">{{ formatPrice(v.montant) }} <span class="text-xs text-gray-400 font-normal">crédité</span></p>
              <p v-if="v.montantEnvoye" class="text-xs text-gray-400">Envoyé : {{ formatPrice(v.montantEnvoye) }} (dont {{ formatPrice(v.frais) }} de frais)</p>
              <p class="text-xs text-gray-500 truncate">{{ v.plan?.produit?.nom || 'Produit' }}</p>
              <p class="text-xs text-gray-400">{{ formatDate(v.createdAt) }}</p>
              <p v-if="v.statut === 'rejete' && v.motifRejet" class="text-xs text-red-500 mt-[2px]">Motif : {{ v.motifRejet }}</p>
            </div>
            <!-- Statut -->
            <span :class="versClass(v.statut)" class="text-[11px] px-3 py-1 rounded-full font-bold whitespace-nowrap">{{ versLabel(v.statut) }}</span>
          </div>
        </div>
      </div>

      <!-- ===================== ONGLET ACHATS (historique en boutique/en ligne) ===================== -->
      <div v-else-if="activeTab === 'achats'">
        <div v-if="isLoadingAchats" class="w-full h-[200px] flex justify-center items-center">
          <div class="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else-if="achats.length === 0" class="w-full bg-white rounded-[10px] shadow-4xl p-[40px] flex flex-col items-center text-center">
          <i class="fa-solid fa-bag-shopping text-4xl text-orange-400 mb-[15px]"></i>
          <h2 class="text-lg font-bold text-gray-700">Aucun achat trouvé</h2>
          <p class="text-sm text-gray-500 mt-[10px]">Vos achats chez Pomme City (en boutique ou en ligne) apparaîtront ici, reconnus via votre numéro WhatsApp.</p>
        </div>

        <div v-else class="w-full bg-white rounded-[10px] shadow-4xl overflow-hidden">
          <div v-for="a in achats" :key="a._id" class="flex items-center gap-[15px] p-[15px] border-b border-gray-50 last:border-b-0">
            <div class="w-[45px] h-[45px] min-w-[45px] rounded-full bg-orange-50 text-orange-500 flex items-center justify-center">
              <i class="fa-solid fa-mobile-screen-button"></i>
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-bold text-gray-800 truncate">{{ a.produits.map(p => p.lotName).join(', ') }}</p>
              <p class="text-xs text-gray-500">{{ a.vendeur?.boutique || 'En ligne' }} • {{ formatDate(a.createdAt) }}</p>
              <p class="text-sm font-bold text-gray-800 mt-[3px]">{{ formatPrice(a.totalApresReduction) }}</p>
            </div>
            <button @click="downloadInvoice(a)" :disabled="downloadingId === a._id" class="px-[12px] py-[8px] rounded-[5px] text-xs font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors whitespace-nowrap flex items-center gap-[6px]">
              <span v-if="downloadingId === a._id" class="w-[12px] h-[12px] border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
              <i v-else class="fa-solid fa-download"></i>
              Facture
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Modale demande produit -->
  <div v-if="showDemandeModal" class="fixed inset-0 bg-black/50 z-[1000000] flex items-center justify-center p-4" @click.self="showDemandeModal = false">
    <div class="bg-white rounded-[10px] shadow-2xl w-full max-w-[420px] overflow-hidden">
      <div class="bg-orange-600 p-[20px] flex justify-between items-center">
        <h3 class="text-white font-bold">Demander un produit</h3>
        <button @click="showDemandeModal = false" class="text-white"><i class="fa-solid fa-times"></i></button>
      </div>
      <form @submit.prevent="submitDemande" class="p-[20px]">
        <p class="text-sm text-gray-500 mb-[15px]">Indiquez le téléphone recherché. Si nous pouvons le trouver, vous serez notifié et pourrez commencer à payer.</p>
        <input v-model="nomDemande" type="text" required class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500 mb-[12px]" placeholder="Ex: iPhone 14 Pro 256Go noir">
        <label class="text-sm font-bold text-gray-700 mb-[5px] block">Boutique de retrait</label>
        <select v-model="boutiqueDemande" required class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500">
          <option value="" disabled>Choisir</option>
          <option v-for="b in boutiques" :key="b" :value="b">{{ b }}</option>
        </select>
        <button type="submit" :disabled="isSubmitting" class="w-full h-[48px] mt-[15px] flex justify-center items-center text-sm font-bold rounded-[5px] bg-orange-600 hover:bg-orange-700 text-white transition-colors">
          <span v-if="!isSubmitting">Envoyer la demande</span>
          <div v-else class="spinner1"><div></div><div></div><div></div><div></div><div></div></div>
        </button>
      </form>
    </div>
  </div>

  <!-- Visionneuse de justificatif -->
  <div v-if="showImage" class="fixed inset-0 bg-black/80 z-[1000001] flex items-center justify-center p-4" @click.self="showImage = false">
    <div class="relative max-w-[90%] max-h-[90%]">
      <button @click="showImage = false" class="absolute -top-[35px] right-0 text-white text-2xl"><i class="fa-solid fa-times"></i></button>
      <img :src="imageUrl" class="max-w-full max-h-[85vh] rounded-[8px] object-contain" alt="justificatif">
    </div>
  </div>

  <FooterComponent />
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useAuthClientStore } from '@/store/useAuthClientStore'

const router = useRouter()
const authClientStore = useAuthClientStore()
const client = computed(() => authClientStore.client)

const activeTab = ref('plans')
const plans = ref([])
const versements = ref([])
const achats = ref([])
const isLoading = ref(true)
const isLoadingVers = ref(false)
const isLoadingAchats = ref(false)
const versLoaded = ref(false)
const achatsLoaded = ref(false)
const downloadingId = ref(null)
const isSubmitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const showDemandeModal = ref(false)
const nomDemande = ref('')
const boutiqueDemande = ref('')
const boutiques = ['Calavi', 'Parakou']
const showImage = ref(false)
const imageUrl = ref('')

const formatPrice = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0)) + ' FCFA'
const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const reste = (plan) => Math.max((plan.prixTotal || 0) - (plan.montantPaye || 0), 0)
const progress = (plan) => plan.prixTotal ? Math.min(Math.round((plan.montantPaye / plan.prixTotal) * 100), 100) : 0

function statutLabel(s) { return { demande: 'En recherche', a_payer: 'À payer', en_cours: 'En cours', termine: 'Payé', vendu: 'Récupéré', annule: 'Annulé' }[s] || s }
function statutClass(s) {
  if (s === 'termine' || s === 'vendu') return 'bg-green-100 text-green-700'
  if (s === 'demande') return 'bg-yellow-100 text-yellow-700'
  if (s === 'annule') return 'bg-red-100 text-red-700'
  return 'bg-orange-100 text-orange-700'
}
function versLabel(s) { return { en_attente: 'En attente', valide: 'Validé', rejete: 'Rejeté' }[s] || s }
function versClass(s) {
  if (s === 'valide') return 'bg-green-100 text-green-700'
  if (s === 'rejete') return 'bg-red-100 text-red-700'
  return 'bg-yellow-100 text-yellow-700'
}

const showMsg = (ref_, msg) => { ref_.value = msg; setTimeout(() => (ref_.value = ''), 4000) }
const openImage = (url) => { if (!url) return; imageUrl.value = url; showImage.value = true }

const fetchPlans = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.get('/api/plans/me')
    plans.value = data || []
  } catch (e) { showMsg(errorMessage, 'Erreur lors du chargement de vos plans.') }
  finally { isLoading.value = false }
}

const fetchVersements = async () => {
  isLoadingVers.value = true
  try {
    const { data } = await axios.get('/api/clients/versements')
    versements.value = data || []
    versLoaded.value = true
  } catch (e) { showMsg(errorMessage, 'Erreur lors du chargement de vos paiements.') }
  finally { isLoadingVers.value = false }
}

const switchToPaiements = () => {
  activeTab.value = 'paiements'
  if (!versLoaded.value) fetchVersements()
}

const fetchAchats = async () => {
  isLoadingAchats.value = true
  try {
    const { data } = await axios.get('/api/sales/me/purchases')
    achats.value = data || []
    achatsLoaded.value = true
  } catch (e) { showMsg(errorMessage, 'Erreur lors du chargement de vos achats.') }
  finally { isLoadingAchats.value = false }
}

const switchToAchats = () => {
  activeTab.value = 'achats'
  if (!achatsLoaded.value) fetchAchats()
}

const downloadInvoice = async (achat) => {
  downloadingId.value = achat._id
  try {
    const response = await axios.get(`/api/sales/${achat._id}/invoice`, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `facture-pomme-city-${achat._id}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    showMsg(errorMessage, 'Erreur lors du téléchargement de la facture.')
  } finally {
    downloadingId.value = null
  }
}

const submitDemande = async () => {
  isSubmitting.value = true
  try {
    await axios.post('/api/plans', { origine: 'demande', nomDemande: nomDemande.value, boutique: boutiqueDemande.value })
    showDemandeModal.value = false
    nomDemande.value = ''
    boutiqueDemande.value = ''
    showMsg(successMessage, 'Demande envoyée ! Nous revenons vers vous.')
    fetchPlans()
  } catch (e) { showMsg(errorMessage, e.response?.data?.error || 'Erreur lors de l\'envoi.') }
  finally { isSubmitting.value = false }
}

const openPayment = (plan) => router.push({ name: 'paiement', params: { id: plan._id } })

const canRemove = (plan) => (plan.montantPaye || 0) === 0 && plan.statut !== 'vendu'

const removePlan = async (plan) => {
  if (!confirm('Retirer ce plan ? Cette action est définitive.')) return
  try {
    await axios.delete(`/api/plans/${plan._id}`)
    showMsg(successMessage, 'Plan retiré.')
    fetchPlans()
    if (versLoaded.value) fetchVersements()
  } catch (e) {
    showMsg(errorMessage, e.response?.data?.error || 'Erreur lors du retrait.')
  }
}

const logout = () => { authClientStore.logout(); router.push('/') }

onMounted(fetchPlans)
</script>
